import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UseFormRegister } from "react-hook-form";

import { Button, Card, Input, Textarea } from "@chef/components";
import { Truck } from "@chef/icons/large";
import { Help, Info, Truck as SmallTruck } from "@chef/icons/small";
import { isEmptyArray } from "@chef/utils/array";
import { BRAND_NAME } from "@chef/constants";
import {
  AppDispatch,
  TimeblocksByRangeWithGeoQuery,
  TimeblocksQuery,
  selectFirstDeliveryTimeblockId,
  selectSignupDeliveryData,
  setFirstDeliveryTimeblockId,
  updateSignupDeliveryData,
} from "@chef/state-management";
import { DigitalKeyProviderInfo, Modal } from "@chef/smart-components";
import { BlockContent, ISection } from "@chef/feature-sanity";
import { isNightTimeDelivery } from "@chef/helpers";
import { isEqualStrings } from "@chef/utils/equal";

import { deliveryTimeStringWthVisibleTimeblocks } from "../utils";
import { ChangeFirstDeliveryDateModal } from "./ChangeFirstDeliveryDateModal";

import { intl } from "./DeliveryInformation.Intl";

interface DeliveryInformationProps {
  availableWeekAndYearTimeblocks: TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"];
  deliveryDetailsRef: React.RefObject<HTMLDivElement>;
  availableTimeblocks: TimeblocksQuery["timeblocksData"]["timeblocks"];

  register: UseFormRegister<any>; // TODO: temp any until we finish automate address transition, swap to IFormInput
  deliveryDetailsModalContent?: ISection | null;
  digitalKeyProvider?: string;
  id: string;
  isApartmentAddress?: boolean;
}

export const DeliveryInformation = ({
  availableWeekAndYearTimeblocks,
  availableTimeblocks,
  deliveryDetailsRef,
  register,
  deliveryDetailsModalContent,
  digitalKeyProvider,
  id,
  isApartmentAddress,
}: DeliveryInformationProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { firstDelivery } = useSelector(selectSignupDeliveryData);
  const firstDeliveryTimeblockIdCurrent = useSelector(
    selectFirstDeliveryTimeblockId,
  );

  const [
    showChangeFirstDeliveryDateModal,
    setShowChangeFirstDeliveryDateModal,
  ] = useState(false);

  const [
    showFirstDeliveryExplanationModal,
    setShowFirstDeliveryExplanationModal,
  ] = useState(false);

  const firstDeliveryOptions =
    availableWeekAndYearTimeblocks.flatMap((week) => week.timeblocks) || [];

  const changeFirstDeliveryHandler = ({
    firstDelivery,
    firstDeliveryTimeblockId,
  }: {
    firstDelivery: string;
    firstDeliveryTimeblockId: number;
  }) => {
    dispatch(updateSignupDeliveryData({ firstDelivery }));
    dispatch(setFirstDeliveryTimeblockId(firstDeliveryTimeblockId));
  };

  const firstDeliveryTimeString = deliveryTimeStringWthVisibleTimeblocks(
    firstDeliveryOptions,
    firstDelivery || "",
    firstDeliveryTimeblockIdCurrent,
  );

  const selectedTimeblock = availableTimeblocks?.find(
    (timeblock) => timeblock.timeblockId === firstDeliveryTimeblockIdCurrent,
  );

  const nightTimeDeliverySelected = isNightTimeDelivery(selectedTimeblock);

  const hasDigitalKeyProvider = isEqualStrings(
    digitalKeyProvider?.toLowerCase(),
    "bekey",
  );

  const shouldShowNightDeliveryAlert =
    isApartmentAddress && nightTimeDeliverySelected && !hasDigitalKeyProvider;

  if (isEmptyArray(availableWeekAndYearTimeblocks)) {
    return (
      <div className="px-4 mb-4 lg:mb-6" id={id}>
        <div className="flex flex-row justify-between p-4 bg-grey-3">
          <div>{intl.FILL_IN_YOUR_POSTNUMBER}</div>
          <Help className="w-5 h-5 text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4" ref={deliveryDetailsRef}>
      <div className="mb-4 lg:mb-6" id="deliveries-delivery-information-card">
        <h2 className="mb-4 text-2xl">
          <strong>{intl.DELIVERY_INFORMATION}</strong>
        </h2>

        <div className="flex flex-row justify-between mb-8 bg-grey-3">
          <section
            className="w-full p-4 pr-0 space-y-4 text-sm"
            id="first-delivery-information-card"
          >
            <div>
              <p>
                <strong>{intl.FIRST_DELIVERY}</strong>
              </p>
              <p>{firstDeliveryTimeString}</p>
            </div>
            <div>
              <p>
                <strong>{intl.NO_BINDING_TIME}</strong>
              </p>
              <p>{intl.START_AND_STOP_DELIVERIES}</p>
            </div>
            <Button
              outlined
              tiny
              type="button"
              className="bg-white"
              onClick={() => setShowChangeFirstDeliveryDateModal(true)}
            >
              {intl.CHANGE_START_DATE}
            </Button>
          </section>
          <button
            className="self-start mt-3.5 mr-4"
            type="button"
            onClick={() => setShowFirstDeliveryExplanationModal(true)}
          >
            <Help className="w-4.5 h-4.5 text-primary" />
          </button>
        </div>

        {BRAND_NAME === "RN" && shouldShowNightDeliveryAlert && (
          <div className="p-4 mb-8 mt-4 text-sm border-1.5 rounded-md bg-highlightBG border-highlight">
            <h3 className="flex items-center gap-2 mb-2 text-sm">
              <Info className="text-highlight" width={18} height={18} />
              <strong>Bemærk venligst!</strong>
            </h3>
            <p>
              Natlevering kan betyde, at din måltidskasse bliver sat udenfor
              uden opsyn i længere tid.
            </p>
          </div>
        )}

        <ChangeFirstDeliveryDateModal
          show={showChangeFirstDeliveryDateModal}
          onClose={() => setShowChangeFirstDeliveryDateModal(false)}
          onSave={changeFirstDeliveryHandler}
          timeblocks={firstDeliveryOptions}
        />

        <div className="flex flex-col gap-4 pb-10" id="delivery-details-inputs">
          <Input
            {...register("telephone")}
            type="tel"
            name="telephone"
            label={intl.TELEPHONE_NUMBER_LABEL}
            maxLength={intl.LENGTH_OF_TELEPHONE_NUMBER}
            autoComplete="off"
          />

          <Input
            {...register("doorCode")}
            name="doorCode"
            label={intl.DOOR_CODE_LABEL}
            maxLength={40}
            autoComplete="off"
            helperText={intl.VOLUNTARY_INPUT}
          />

          <DigitalKeyProviderInfo
            digitalKeyProvider={digitalKeyProvider}
            className="my-4"
          />

          <Textarea
            {...register("deliveryInstructionsToDriver")}
            name="deliveryInstructionsToDriver"
            label={intl.INSTRUCTIONS_TO_DRIVER_LABEL}
            maxLength={255}
            helperText={intl.EXAMPLE_INSTRUCTIONS_TO_DRIVER}
          />
        </div>
      </div>
      <Modal
        name="first-delivery-explanation-modal"
        open={showFirstDeliveryExplanationModal}
        onClose={() => setShowFirstDeliveryExplanationModal(false)}
      >
        <Card className="flex flex-col items-center justify-between">
          <div className="hidden md:inline">
            <SmallTruck className="w-20 h-20 mt-16" />
          </div>
          <div className="inline md:hidden">
            <Truck className="w-32 h-32 mt-8" />
          </div>
          <h1 className="mt-6 text-xl md:text-3xl">
            <strong>{intl.DELIVERY_INFORMATION}</strong>
          </h1>
          <div className="max-w-2xl mx-4 mt-6">
            {showFirstDeliveryExplanationModal &&
              deliveryDetailsModalContent && (
                <BlockContent
                  body={deliveryDetailsModalContent.content.body}
                  prose={{ disabled: true }}
                />
              )}
          </div>
        </Card>
      </Modal>
    </div>
  );
};
